import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";

import Header from "../../../components/common/Header";
import Footer from "../../../components/common/Footer";
import BackToTop from "../../../components/common/BackToTop";
import { useEvent } from "../../../services/eventService";

const EventDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null
  );
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const { data: event, isLoading, error } = useEvent(id!);

  // Trigger animations when data loads
  useEffect(() => {
    if (!isLoading && event) {
      setIsDataLoaded(true);
    }
  }, [isLoading, event]);

  if (isLoading) {
    return (
      <div className="min-h-screen w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
        <Header />
        <main className="py-16 px-4 sm:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Loading event details...
            </p>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
        <Header />
        <main className="py-16 px-4 sm:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-4">Event Not Found</h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
              {error
                ? "Failed to load event details."
                : "The event you're looking for doesn't exist."}
            </p>
            <button
              onClick={() => navigate("/events")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Back to Events
            </button>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
      <Header />
      <main>
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative h-96 md:h-[500px] overflow-hidden bg-gradient-to-br from-emerald-600 via-stone-600 to-emerald-700"
        >
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="max-w-4xl"
            >
              <div className="flex items-center justify-center mb-4">
                <span
                  className={`px-4 py-2 rounded-full text-sm font-bold text-white ${event.status === "completed"
                      ? "bg-green-500"
                      : event.status === "upcoming"
                        ? "bg-emerald-500"
                        : "bg-stone-500"
                    }`}
                >
                  {t(`events.status.${event.status}`)}
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
                {event.title}
              </h1>
              <p className="text-xl text-white/90 mb-6">{event.description}</p>
              <div className="flex flex-wrap justify-center gap-6 text-white/80">
                <div className="flex items-center">
                  <span className="text-2xl mr-2">📅</span>
                  <span>{event.date}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-2xl mr-2">📍</span>
                  <span>{event.location}</span>
                </div>
                {event.attendees && (
                  <div className="flex items-center">
                    <span className="text-2xl mr-2">👥</span>
                    <span>{event.attendees} attendees</span>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Event Overview */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="py-16 px-4 sm:px-16"
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold mb-6 text-emerald-600 dark:text-emerald-400">
                  📋 Event Overview
                </h2>
                {event.detailedDescription && (
                  <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                    {event.detailedDescription}
                  </p>
                )}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                      Duration
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      {event.duration}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                      Organizer
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      {event.organizer}
                    </p>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Category
                  </h4>
                  <span className="bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200 px-3 py-1 rounded-full text-sm font-medium">
                    {event.category}
                  </span>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-6 text-emerald-600 dark:text-emerald-400">
                  🎯 Event Objectives
                </h3>
                {event.objectives && (
                  <ul className="space-y-3">
                    {event.objectives.map((objective, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: 20 }}
                        animate={
                          isDataLoaded
                            ? { opacity: 1, x: 0 }
                            : { opacity: 0, x: 20 }
                        }
                        viewport={{ once: true, amount: 0.1 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="flex items-start"
                      >
                        <span className="text-green-500 mr-3 mt-1">✓</span>
                        <span className="text-gray-600 dark:text-gray-400">
                          {objective}
                        </span>
                      </motion.li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </motion.section>

        {/* Event Highlights */}
        {event.highlights && event.highlights.length > 0 && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={isDataLoaded ? { opacity: 1 } : { opacity: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8 }}
            className="py-16 px-4 sm:px-16 bg-gradient-to-br from-emerald-50 to-stone-50 dark:from-gray-800 dark:to-gray-900"
          >
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-stone-600 dark:text-stone-400">
                ✨ Event Highlights
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {event.highlights.map((highlight, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={
                      isDataLoaded
                        ? { opacity: 1, y: 0 }
                        : { opacity: 0, y: 30 }
                    }
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-700 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <div className="text-3xl mb-4">🎉</div>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      {highlight}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>
        )}

        {/* Event Outcomes */}
        {event.outcomes && event.outcomes.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 50 }}
            animate={
              isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }
            }
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8 }}
            className="py-16 px-4 sm:px-16"
          >
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-emerald-600 dark:text-emerald-400">
                📊 Event Outcomes & Impact
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {event.outcomes.map((outcome, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                    animate={
                      isDataLoaded
                        ? { opacity: 1, x: 0 }
                        : { opacity: 0, x: index % 2 === 0 ? -30 : 30 }
                    }
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-gradient-to-r from-emerald-50 to-stone-50 dark:from-emerald-900/20 dark:to-stone-900/20 p-6 rounded-2xl border-l-4 border-emerald-500 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="flex items-start">
                      <span className="text-emerald-500 text-2xl mr-4">🏆</span>
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {outcome}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>
        )}

        {/* Event Gallery */}
        {event.gallery && event.gallery.length > 0 && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={isDataLoaded ? { opacity: 1 } : { opacity: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8 }}
            className="py-16 px-4 sm:px-16 bg-gradient-to-br from-emerald-50 to-stone-50 dark:from-gray-800 dark:to-gray-900"
          >
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900 dark:text-gray-100">
                📸 Event Gallery
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {event.gallery.map((image, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={
                      isDataLoaded
                        ? { opacity: 1, scale: 1 }
                        : { opacity: 0, scale: 0.8 }
                    }
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="relative group cursor-pointer overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300"
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img
                      src={image}
                      alt={`${event.title} - Image ${index + 1}`}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                      <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-lg font-semibold">
                        View
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>
        )}

        {/* Event Videos */}
        {event.videos && event.videos.length > 0 && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={isDataLoaded ? { opacity: 1 } : { opacity: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8 }}
            className="py-16 px-4 sm:px-16"
          >
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-red-600 dark:text-red-400">
                🎥 Event Videos
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {event.videos.map((video, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={
                      isDataLoaded
                        ? { opacity: 1, y: 0 }
                        : { opacity: 0, y: 30 }
                    }
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
                  >
                    <video
                      controls
                      className="w-full h-48 object-cover"
                      poster={`https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=300&fit=crop`}
                    >
                      <source src={video.url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                        Event Highlights - Part {index + 1}
                      </h3>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>
        )}

        {/* Event Report */}
        {event.eventReport && (
          <motion.section
            initial={{ opacity: 0, y: 50 }}
            animate={
              isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }
            }
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8 }}
            className="py-16 px-4 sm:px-16 bg-gradient-to-r from-emerald-50 to-stone-50 dark:from-emerald-900/20 dark:to-stone-900/20"
          >
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-emerald-600 dark:text-emerald-400">
                📝 Event Report
              </h2>
              <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl">
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed italic">
                  "{event.eventReport}"
                </p>
              </div>
            </div>
          </motion.section>
        )}

        {/* Call to Action */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={isDataLoaded ? { opacity: 1 } : { opacity: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="py-20 px-4 sm:px-16 bg-gradient-to-r from-emerald-500 via-stone-500 to-emerald-600 text-white relative overflow-hidden"
        >
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={
                isDataLoaded
                  ? { scale: 1, opacity: 1 }
                  : { scale: 0.9, opacity: 0 }
              }
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <span className="text-6xl">🎉</span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6">
              Join Our Next Event
            </h2>
            <p className="text-xl md:text-2xl mb-10 opacity-95 font-light leading-relaxed max-w-2xl mx-auto">
              Stay connected and be part of our community's exciting journey!
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/events")}
                className="bg-white text-emerald-600 px-10 py-5 rounded-2xl font-bold text-lg shadow-2xl hover:bg-gray-50 transition-all duration-300 border-2 border-transparent hover:border-white/50"
              >
                View All Events 🎊
              </motion.button>
              {event.registrationLink && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-white hover:text-emerald-600 transition-all duration-300 shadow-xl"
                >
                  Register for Events ✨
                </motion.button>
              )}
            </div>
          </div>
        </motion.section>

        {/* Image Preview Modal */}
        <AnimatePresence>
          {selectedImageIndex !== null && event.gallery && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedImageIndex(null)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="relative max-w-4xl max-h-full"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={event.gallery[selectedImageIndex]}
                  alt={`${event.title} - Image ${selectedImageIndex + 1}`}
                  className="max-w-full max-h-full object-contain"
                />

                {/* Close Button */}
                <button
                  onClick={() => setSelectedImageIndex(null)}
                  className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>

                {/* Navigation Buttons */}
                {event.gallery.length > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setSelectedImageIndex(
                          selectedImageIndex === 0
                            ? event.gallery!.length - 1
                            : selectedImageIndex - 1
                        )
                      }
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 rounded-full p-3 transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() =>
                        setSelectedImageIndex(
                          selectedImageIndex === event.gallery!.length - 1
                            ? 0
                            : selectedImageIndex + 1
                        )
                      }
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 rounded-full p-3 transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </>
                )}

                {/* Image Counter */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black/50 px-3 py-1 rounded-full text-sm">
                  {selectedImageIndex + 1} / {event.gallery.length}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default EventDetail;
